/**
 * AI output languages shared by API and web (PREC-3485).
 *
 * The list is the 24 official EU languages (ISO 639-1 codes). It drives every
 * "AI output language" setting — deep research prompts, signal configs, and
 * lead-scoring reasoning — so the UI dropdowns and the API validation always
 * agree. A null/undefined language means English (the historical default).
 */
export const OUTPUT_LANGUAGES = [
	"bg",
	"hr",
	"cs",
	"da",
	"nl",
	"en",
	"et",
	"fi",
	"fr",
	"de",
	"el",
	"hu",
	"ga",
	"it",
	"lv",
	"lt",
	"mt",
	"pl",
	"pt",
	"ro",
	"sk",
	"sl",
	"es",
	"sv",
] as const;

export type OutputLanguageCode = (typeof OUTPUT_LANGUAGES)[number];

/**
 * Maps a supported language code to its English display name. The English name
 * is what gets embedded in prompt directives ("Write ALL content in German"),
 * and the web renders it as the dropdown label.
 */
export const OUTPUT_LANGUAGE_NAMES: Record<OutputLanguageCode, string> = {
	bg: "Bulgarian",
	hr: "Croatian",
	cs: "Czech",
	da: "Danish",
	nl: "Dutch",
	en: "English",
	et: "Estonian",
	fi: "Finnish",
	fr: "French",
	de: "German",
	el: "Greek",
	hu: "Hungarian",
	ga: "Irish",
	it: "Italian",
	lv: "Latvian",
	lt: "Lithuanian",
	mt: "Maltese",
	pl: "Polish",
	pt: "Portuguese",
	ro: "Romanian",
	sk: "Slovak",
	sl: "Slovenian",
	es: "Spanish",
	sv: "Swedish",
};

export const DEFAULT_OUTPUT_LANGUAGE: OutputLanguageCode = "en";

/**
 * Resolves a language ISO code to its English display name. Falls back to
 * "English" for unknown/undefined codes (the historical default).
 */
export const resolveOutputLanguageName = (languageCode?: string | null): string => {
	const code = (languageCode || DEFAULT_OUTPUT_LANGUAGE) as OutputLanguageCode;

	return OUTPUT_LANGUAGE_NAMES[code] || OUTPUT_LANGUAGE_NAMES[DEFAULT_OUTPUT_LANGUAGE];
};
